import PropTypes from 'prop-types'
import { Form, Select } from 'antd'
import { useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'

const SelectInput = ({
  isMulti = false,
  isRequired = true,
  onChange = () => {},
  options = [],
  // selectedOptions = [],
  // optionCache,
  onInputChange = () => {},
  onClear = () => {},
  onMenuClose = () => {},
  filterOption = undefined,
  formatLabel = undefined,
  label,
  name,
  value,
  initialValue,
  placeholder,
  formatOption,
  showLoader = false,
  onMenuOpen = () => {},
  wrapperClass = '',
  selectClass = '',
  selectPopupClass = '',
  popupMatchSelectWidth = undefined,
  allowClear = true,
  showSearch = true,
  disabled = false,
  hideLabel = false,
  errorHelp = false,
  errorMsg = '',
  getPopupContainer = undefined,
  maxTagCount = undefined,
  maxTagPlaceholder = undefined,
  // Lets a caller show a fetch failure inside the dropdown instead of raising a
  // toast - search-as-you-type options would otherwise toast on every keystroke.
  // Default undefined, which is antd's own behaviour, so existing callers are
  // unaffected.
  notFoundContent = undefined,
}) => {
  const optionCacheRef = useRef(new Map())
  const intl = useIntl()

  // Test ID for E2E locators, derived from the form field name so it is stable
  // across refactors of this component. `name` may be an array for nested antd
  // form fields (PropTypes allows string | array).
  const testId = Array.isArray(name) ? name.join('.') : name

  useEffect(() => {
    options.forEach((option) => {
      optionCacheRef.current.set(option.value, option)
    })
  }, [options])

  /* useEffect(() => {
    selectedOptions.forEach((option) => {
      optionCacheRef.current.set(option.value, option)
    })
  }, [selectedOptions]) */

  return (
    <Form.Item
      name={name}
      label={label ? label : ''}
      className={`${wrapperClass} ${hideLabel ? 'hide-label' : ''}`}
      style={{ marginBottom: 0, minWidth: 0 }}
      initialValue={initialValue}
      rules={
        isRequired
          ? [
              {
                required: true,
                message:
                  errorHelp === false
                    ? ''
                    : errorMsg.length > 0
                      ? errorMsg
                      : intl.formatMessage(
                          { id: 'validation.requiredField' },
                          { label: label }
                        ),
              },
            ]
          : undefined
      }
      {...(errorHelp === false ? { help: '' } : {})}
    >
      <Select
        {...(notFoundContent !== undefined ? { notFoundContent } : {})}
        data-testid={testId}
        mode={isMulti ? 'multiple' : undefined}
        {...(isMulti && maxTagCount !== undefined ? { maxTagCount } : {})}
        {...(isMulti && maxTagPlaceholder !== undefined
          ? {
              // antd's own `omittedValues[i].label` is the *rendered* tag content (i.e. whatever
              // `labelRender` below returned, which can be JSX from `formatLabel`) - resolve back
              // to the original cached option so consumers get a plain-text `label` to work with.
              maxTagPlaceholder: (omittedValues) =>
                maxTagPlaceholder(
                  omittedValues.map(
                    (v) => optionCacheRef.current.get(v.value) || v
                  )
                ),
            }
          : {})}
        //value={value}
        {...(value !== undefined ? { value } : {})}
        {...(popupMatchSelectWidth !== undefined
          ? { popupMatchSelectWidth: popupMatchSelectWidth }
          : {})}
        options={(() => {
          const normalizedOptions = options.map((option) => {
            return {
              ...option,
              label: option.label,
              value: option.value,
            }
          })

          return normalizedOptions
        })()}
        // selectedOptions={selectedOptions}
        placeholder={placeholder || label}
        className={selectClass}
        classNames={{
          popup: {
            root: selectPopupClass,
          },
        }}
        aria-label={label ? label : placeholder}
        loading={showLoader}
        allowClear={allowClear}
        showSearch={showSearch}
        disabled={disabled}
        onClear={onClear}
        {...(showSearch
          ? {
              onSearch: (value) => {
                onInputChange(value)
              },
            }
          : {})}
        onOpenChange={(open) => {
          if (open) {
            onMenuOpen()
          } else {
            onMenuClose()
          }
        }}
        getPopupContainer={
          getPopupContainer
            ? (trigger) => getPopupContainer(trigger)
            : undefined
        }
        filterOption={(input, option) => {
          if (filterOption) {
            return filterOption(input, option)
          }

          return option.label?.toLowerCase().includes(input.toLowerCase())
        }}
        /* onChange={(value) => {
          if (isMulti) {
            const selectedOptions = options.filter((o) =>
              value.includes(o.value)
            )
            onChange(selectedOptions)
            return
          }

          const option = options.find((o) => o.value === value)
          onChange(option)
        }} */
        onChange={(value) => {
          if (isMulti) {
            const selectedOptions = value
              .map((id) => optionCacheRef.current.get(id))
              .filter(Boolean)

            onChange(selectedOptions)
            return
          }

          onChange(optionCacheRef.current.get(value))
        }}
        optionRender={(option) => {
          if (!option?.data) return null

          const data = option.data
          return formatOption ? formatOption(data) : data.label
        }}
        /* labelRender={(selectedOption) => {
          if (!selectedOption?.value) return null

          const orgOption = optionCache
            ? optionCache[selectedOption.value]
            : options.find((o) => {
                return o.value === selectedOption.value
              })

          if (!orgOption) return selectedOption.label

          return formatLabel ? formatLabel(orgOption) : orgOption.label
        }} */
        labelRender={(selectedOption) => {
          const option = optionCacheRef.current.get(selectedOption.value)

          if (!option) {
            return selectedOption.label || selectedOption.value
          }

          return formatLabel ? formatLabel(option) : option.label
        }}
        /* labelRender={(selectedOption) => {
          if (!selectedOption?.value) return null

          const option = options.find((o) => {
            // console.log(`Compare | ${o.value} === ${selectedOption.value} = ${o.value === selectedOption.value}`)
            return o.value === selectedOption.value
          })
          
          return formatLabel ? formatLabel(option) : selectedOption.label
        }} */

        /* labelRender={(selectedOption) => {
          const value = typeof selectedOption === 'string' ? selectedOption : selectedOption?.value
          console.log(`LR: ${value} | Type: ${typeof(value)}`)
          if(!value) return null

          const option = options.find((o) => o.value === value)
          return formatLabel ? formatLabel(option) : option?.label
        }} */
      />
    </Form.Item>
  )
}

SelectInput.propTypes = {
  className: PropTypes.string,
  isMulti: PropTypes.bool,
  onChange: PropTypes.func,
  label: PropTypes.string,
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  placeholder: PropTypes.string,
  isRequired: PropTypes.bool,
  value: PropTypes.any,
  initialValue: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  options: PropTypes.array,
  // selectedOptions: PropTypes.any,
  // optionCache: PropTypes.object,
  formatOption: PropTypes.func,
  formatLabel: PropTypes.func,
  showLoader: PropTypes.bool,
  wrapperClass: PropTypes.string,
  selectClass: PropTypes.string,
  selectPopupClass: PropTypes.string,
  popupMatchSelectWidth: PropTypes.bool,
  onMenuOpen: PropTypes.func,
  onInputChange: PropTypes.func,
  onClear: PropTypes.func,
  onMenuClose: PropTypes.func,
  allowClear: PropTypes.bool,
  showSearch: PropTypes.bool,
  disabled: PropTypes.bool,
  hideLabel: PropTypes.bool,
  errorHelp: PropTypes.bool,
  errorMsg: PropTypes.string,
  maxTagCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxTagPlaceholder: PropTypes.func,
}

export default SelectInput
